import React, { useState } from 'react';
import axios from 'axios';
import ObituaryForm from './ObituaryForm';

const buildAbsolutePdfUrl = (pdfUrl) => {
  if (!pdfUrl) return null;
  if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://')) {
    return pdfUrl;
  }
  const prefix = pdfUrl.startsWith('/') ? '' : '/';
  return `${window.location.origin}${prefix}${pdfUrl}`;
};

const Redeem = () => {
  const [step, setStep] = useState('enter-code'); // 'enter-code', 'show-form', 'show-obituary', 'already-redeemed', 'rate-limited'
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [redemptionInfo, setRedemptionInfo] = useState(null);
  const [obituary, setObituary] = useState(null);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [satisfactionRating, setSatisfactionRating] = useState(null);
  const [savedFormData, setSavedFormData] = useState(null);
  const [purchaseId, setPurchaseId] = useState(null);
  const [isRetry, setIsRetry] = useState(false);
  const [existingObituaryId, setExistingObituaryId] = useState(null);

  const handleDownloadPdf = (pdfUrl) => {
    const absoluteUrl = buildAbsolutePdfUrl(pdfUrl);
    if (!absoluteUrl) return;
    const downloadWindow = window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
    if (downloadWindow) {
      downloadWindow.focus();
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/redeem/validate', { code: code.toUpperCase() });
      
      if (response.data.valid) {
        setPurchaseId(response.data.purchaseId);
        setStep('show-form');
      } else if (response.data.alreadyRedeemed) {
        setRedemptionInfo(response.data);
        if (response.data.canAccess) {
          const obituaryData = response.data.obituary;
          setObituary(obituaryData);
          // Store form data and purchase_id if available
          if (obituaryData.form_data) {
            setSavedFormData(obituaryData.form_data);
          }
          if (obituaryData.purchase_id) {
            setPurchaseId(obituaryData.purchase_id);
          }
          setStep('show-obituary');
        } else {
          setStep('already-redeemed');
        }
      }
    } catch (err) {
      if (err.response?.status === 429 && err.response?.data?.rateLimited) {
        setRateLimitInfo({
          message: err.response.data.error,
          remainingMinutes: err.response.data.remainingMinutes
        });
        setStep('rate-limited');
      } else {
        setError(err.response?.data?.error || 'Invalid code. Please check and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleObituarySuccess = (obituaryData) => {
    setObituary(obituaryData);
    // Store form data and purchase_id for potential re-use
    if (obituaryData.form_data) {
      setSavedFormData(obituaryData.form_data);
    }
    if (obituaryData.purchase_id) {
      setPurchaseId(obituaryData.purchase_id);
    }
    // If this was a retry (retry_count > 0), reset satisfaction rating so they can rate the new version
    // Otherwise, keep existing rating if it exists
    if ((obituaryData.retry_count || 0) > 0) {
      setSatisfactionRating(null); // Allow them to rate the retry
    } else if (obituaryData.satisfaction_rating) {
      setSatisfactionRating(obituaryData.satisfaction_rating);
    }
    // Reset retry flag after successful generation
    setIsRetry(false);
    setExistingObituaryId(null);
    setStep('show-obituary');
  };

  const handleSatisfactionSelect = async (rating) => {
    setSatisfactionRating(rating);
    
    // Save satisfaction rating to backend
    if (obituary?.id) {
      try {
        await axios.post('/api/satisfaction', {
          obituaryId: obituary.id,
          rating: rating
        });
        // Update obituary state with new rating
        setObituary(prev => ({
          ...prev,
          satisfaction_rating: rating
        }));
      } catch (err) {
        console.error('Error saving satisfaction rating:', err);
      }
    }
    
    // If not happy, check if they can retry (only once)
    if (rating !== 'happy') {
      const retryCount = obituary?.retry_count || 0;
      
      // Only allow retry if they haven't retried yet
      if (retryCount === 0) {
        // Store existing obituary ID for overwriting
        if (obituary?.id) {
          setExistingObituaryId(obituary.id);
        }
        // Mark as retry and navigate to form
        setIsRetry(true);
        setStep('show-form');
        // Scroll to form
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
      // If they've already retried, just show the rating (no form navigation)
    }
  };

  const handleObituaryError = (errorMessage) => {
    setError(errorMessage);
  };

  // Render enter code form
  if (step === 'enter-code') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="glass-card text-center mb-4">
              <h1 className="commercial-heading mb-4">Redeem Your Code</h1>
              <p className="commercial-subheading mb-4">
                Enter your redemption code to create an obituary
              </p>
              
              <form onSubmit={handleCodeSubmit}>
                <div className="mb-4">
                  <input
                    type="text"
                    className="form-control form-control-lg text-center"
                    placeholder="OBIT-XXXX-XXXX-XXXX"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    maxLength="19"
                    required
                    style={{ letterSpacing: '2px', fontFamily: 'monospace' }}
                  />
                </div>
                
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                
                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100"
                  disabled={loading || code.length < 19}
                >
                  {loading ? 'Validating...' : 'Continue'}
                </button>
              </form>
            </div>

            <div className="glass-card text-center" style={{ background: 'rgba(255, 255, 255, 0.9)' }}>
              <h2 className="commercial-heading mb-3" style={{ fontSize: '1.4rem', color: '#1e3a8a' }}>
                Don't have a code?
              </h2>
              <p className="mb-3" style={{ color: '#1a1a1a' }}>
                Purchase a package to receive your unique redemption codes instantly.
              </p>
              <a
                href="/?checkout=true"
                className="d-inline-flex align-items-center justify-content-center px-4 py-3"
                style={{
                  fontWeight: 600,
                  color: '#0f172a',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgba(15, 23, 42, 0.15)',
                  borderRadius: '999px',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 12px 30px rgba(15, 23, 42, 0.2)',
                  textDecoration: 'none'
                }}
              >
                Get Some Codes
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render form
  if (step === 'show-form') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-8">
            <div className="glass-card text-center mb-4">
              <h1 className="commercial-heading mb-3">Create Obituary</h1>
              <p className="commercial-subheading mb-0">
                Fill in the details to generate a professional obituary
              </p>
              <p className="mt-2">
                <span className="badge bg-success">Code: {code}</span>
              </p>
            </div>

            {isRetry && (
              <div className="glass-card mb-4 text-center" style={{ background: 'rgba(30, 64, 175, 0.1)', border: '2px solid rgba(30, 64, 175, 0.3)' }}>
                <h3 className="mb-3" style={{ color: '#1e40af' }}>Let's Get This Right!</h3>
                <p className="mb-0" style={{ color: '#1a1a1a' }}>
                  We want to make sure you're completely satisfied. Your form has been pre-filled with your previous responses. 
                  Please make any changes you'd like, and we'll generate a new obituary that will replace the previous one.
                </p>
              </div>
            )}

            <ObituaryForm
              code={code}
              onSuccess={handleObituarySuccess}
              onError={handleObituaryError}
              loading={loading}
              initialFormData={savedFormData}
              isRetry={isRetry}
              existingObituaryId={existingObituaryId}
            />
          </div>
        </div>
      </div>
    );
  }

  // Render rate limited message
  if (step === 'rate-limited') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="glass-card text-center">
              <div className="mb-4">
                <i className="bi bi-shield-exclamation" style={{ fontSize: '4rem', color: '#dc3545' }}></i>
              </div>
              <h2 className="commercial-heading mb-3">Too Many Attempts</h2>
              <p className="commercial-subheading mb-4">
                {rateLimitInfo?.message || 'Too many failed attempts. Please try again later.'}
              </p>
              <div className="alert alert-warning" role="alert">
                <i className="bi bi-clock me-2"></i>
                For security purposes, code validation has been temporarily disabled for your IP address.
              </div>
              <p className="text-muted mt-4 small">
                If you're having trouble with your code, please check your email or contact support.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render already redeemed message
  if (step === 'already-redeemed') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="glass-card text-center">
              <div className="mb-4">
                <i className="bi bi-exclamation-circle" style={{ fontSize: '4rem', color: '#f59e0b' }}></i>
              </div>
              <h2 className="commercial-heading mb-3">Code Already Redeemed</h2>
              <p className="commercial-subheading mb-4">
                This code was redeemed on {new Date(redemptionInfo.redeemedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <p>
                {redemptionInfo.message}
              </p>
              <button
                className="btn btn-primary mt-4"
                onClick={() => {
                  setStep('enter-code');
                  setCode('');
                  setError('');
                  setRedemptionInfo(null);
                }}
              >
                Try Another Code
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render obituary
  if (step === 'show-obituary') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-8">
            <div className="glass-card text-center mb-4">
              <div className="mb-3">
                <i className="bi bi-check-circle" style={{ fontSize: '4rem', color: '#10b981' }}></i>
              </div>
              <h1 className="commercial-heading mb-3">Obituary Generated</h1>
              <p className="commercial-subheading">
                Your professional obituary has been created and is ready for use
              </p>
            </div>

            <div className="glass-card" style={{ background: 'rgba(255, 255, 255, 0.95)' }}>
              <div className="text-center mb-4">
                <h2 style={{ color: '#1e40af' }}>{obituary.deceased_name}</h2>
                {(obituary.birth_date || obituary.death_date) && (
                  <p style={{ color: '#666', fontStyle: 'italic', fontSize: '1.1rem' }}>
                    {obituary.birth_date && new Date(obituary.birth_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    {obituary.birth_date && obituary.death_date && ' — '}
                    {obituary.death_date && new Date(obituary.death_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
              </div>
              
              <div style={{ color: '#1a1a1a', whiteSpace: 'pre-wrap', textAlign: 'justify', lineHeight: '1.8' }}>
                {obituary.full_text}
              </div>

              <div 
                className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3 flex-wrap mt-5 pt-4 text-center"
                style={{ borderTop: '1px solid #ddd' }}
              >
                {obituary.pdf_available !== false && (
                  <button
                    type="button"
                    className="btn btn-primary btn-lg px-4 shadow-sm"
                    onClick={() => handleDownloadPdf(obituary.pdf_url)}
                  >
                    <i className="bi bi-download me-2"></i>
                    Download PDF
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-lg px-4 shadow-sm"
                  style={{
                    backgroundColor: 'rgb(100, 100, 100)',
                    borderColor: 'rgb(100, 100, 100)',
                    color: '#ffffff',
                    borderWidth: '1px',
                    fontWeight: 600
                  }}
                  onClick={() => {
                    setStep('enter-code');
                    setCode('');
                    setError('');
                    setObituary(null);
                  }}
                >
                  Create Another
                </button>
              </div>

              {/* Satisfaction Survey */}
              {!satisfactionRating && (
                <div className="text-center mt-5 pt-4" style={{ borderTop: '1px solid #ddd' }}>
                  <h3 className="mb-3" style={{ color: '#1e40af', fontSize: '1.3rem' }}>How We Did?</h3>
                  <div className="d-flex justify-content-center gap-4 mb-3">
                    <button
                      type="button"
                      className="btn btn-link p-0 border-0"
                      onClick={() => handleSatisfactionSelect('sad')}
                      style={{ fontSize: '3rem', textDecoration: 'none', background: 'transparent' }}
                      title="Sad"
                    >
                      😢
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0 border-0"
                      onClick={() => handleSatisfactionSelect('indifferent')}
                      style={{ fontSize: '3rem', textDecoration: 'none', background: 'transparent' }}
                      title="Indifferent"
                    >
                      😐
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0 border-0"
                      onClick={() => handleSatisfactionSelect('happy')}
                      style={{ fontSize: '3rem', textDecoration: 'none', background: 'transparent' }}
                      title="Happy"
                    >
                      😊
                    </button>
                  </div>
                </div>
              )}

              {/* Already Rated - Show Rating Status */}
              {satisfactionRating && (
                <div className="text-center mt-5 pt-4" style={{ borderTop: '1px solid #ddd' }}>
                  {satisfactionRating === 'happy' ? (
                    <>
                      {(obituary?.retry_count || 0) > 0 ? (
                        <p style={{ color: '#10b981', fontSize: '1.1rem', fontWeight: '600' }}>
                          <i className="bi bi-heart-fill me-2"></i>
                          We're so glad we could get it right! Thank you for your patience.
                        </p>
                      ) : (
                        <p style={{ color: '#10b981', fontSize: '1.1rem', fontWeight: '600' }}>
                          <i className="bi bi-heart-fill me-2"></i>
                          Thank you for your feedback! We're glad you're satisfied.
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p style={{ color: '#1a1a1a', fontSize: '1rem', marginBottom: '1rem' }}>
                        You rated this obituary as: <strong>{satisfactionRating === 'sad' ? '😢 Sad' : '😐 Indifferent'}</strong>
                      </p>
                      {(obituary?.retry_count || 0) > 0 ? (
                        <>
                          <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                            We're sorry we couldn't get it quite right this time.
                          </p>
                          <p style={{ color: '#666', fontSize: '0.95rem' }}>
                            Remember, your package includes 5 codes total. You can use another code to try again with different or additional information that might help us create a better obituary. If you need assistance, please contact our support team.
                          </p>
                        </>
                      ) : (
                        <p style={{ color: '#666', fontSize: '0.95rem' }}>
                          Thank you for your feedback. We appreciate you helping us improve our service.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
              <div className="text-center mt-4" style={{ fontSize: '0.9rem', color: '#666' }}>
                <p>
                  <i className="bi bi-info-circle me-2"></i>
                  This obituary and PDF will be accessible for 90 days
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default Redeem;

